import * as React from "react";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {styled} from "@mui/material";
import Loading from "../../components/loading";
import {toast} from "react-toastify";
import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";
import downloadCsv from "../../lib/downloadCsv";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

const REQUIRED_HEADERS = [
    "hp_campaign_id",
    "monthly_cap",
    "daily_cap",
    "cost_per_lead",
    "pay_per_impression",
] as const;

const HEADER_LINE = REQUIRED_HEADERS.join("\t");

type CapsMode = "now" | "firstOfMonth" | "schedule";

function defaultScheduleApplyAt(): string {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(1, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface CapRow {
    hpCampaignId: number;
    monthlyCap: number | null;
    dailyCap: number | null;
    costPerLead: number | null;
    payPerImpression: number | null;
}

interface BulkResult {
    updatedCount: number;
    deletedQueueCount: number;
    messages: string[];
    errors: string[];
}

interface PendingCap {
    hpCampaignId: number;
    school: string;
    broker: string;
    monthlyCap: number | null;
    dailyCap: number | null;
    costPerLead: number | null;
    payPerImpression: number | null;
    applyAt: string | null;
    createdAt: string | null;
}

function parseOptionalNumber(value: string, field: string, lineNo: number): number | null {
    const trimmed = value.trim();
    if (trimmed === "") {
        return null;
    }
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
        throw new Error(`Line ${lineNo}: Invalid ${field}: '${value}'`);
    }
    return Number(trimmed);
}

export function parseCapsTsv(text: string): {rows: CapRow[]; parseErrors: string[]} {
    const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim() !== "");
    if (lines.length < 2) {
        return {rows: [], parseErrors: ["File must include a header row and at least one data row"]};
    }

    const delimiter = lines[0].includes("\t") ? "\t" : ",";
    const headers = lines[0].split(delimiter).map((h) => h.trim());
    const indexes: Record<string, number> = {};
    for (const required of REQUIRED_HEADERS) {
        const idx = headers.indexOf(required);
        if (idx < 0) {
            return {rows: [], parseErrors: [`Missing header field: ${required}`]};
        }
        indexes[required] = idx;
    }

    const rows: CapRow[] = [];
    const parseErrors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
        const lineNo = i + 1;
        const cells = lines[i].split(delimiter);
        try {
            const idRaw = (cells[indexes.hp_campaign_id] ?? "").trim();
            if (!/^\d+$/.test(idRaw)) {
                throw new Error(`Invalid hp_campaign_id: '${idRaw}'`);
            }
            rows.push({
                hpCampaignId: Number(idRaw),
                monthlyCap: parseOptionalNumber(cells[indexes.monthly_cap] ?? "", "monthly_cap", lineNo),
                dailyCap: parseOptionalNumber(cells[indexes.daily_cap] ?? "", "daily_cap", lineNo),
                costPerLead: parseOptionalNumber(cells[indexes.cost_per_lead] ?? "", "cost_per_lead", lineNo),
                payPerImpression: parseOptionalNumber(
                    cells[indexes.pay_per_impression] ?? "",
                    "pay_per_impression",
                    lineNo
                ),
            });
        } catch (e: any) {
            parseErrors.push(`Line ${lineNo}: ${e?.message || e}`);
        }
    }

    return {rows, parseErrors};
}

function fmtNum(v: number | null | undefined): string {
    return v == null ? "" : String(v);
}

function pendingToCsvRows(rows: PendingCap[], includeApplyAt: boolean): any[][] {
    const header = [
        "hp_campaign_id",
        "school",
        "broker",
        "monthly_cap",
        "daily_cap",
        "cost_per_lead",
        "pay_per_impression",
        ...(includeApplyAt ? ["apply_at"] : []),
        "created_at",
    ];
    return [
        header,
        ...rows.map((r) => [
            r.hpCampaignId,
            r.school,
            r.broker,
            fmtNum(r.monthlyCap),
            fmtNum(r.dailyCap),
            fmtNum(r.costPerLead),
            fmtNum(r.payPerImpression),
            ...(includeApplyAt ? [r.applyAt || ""] : []),
            r.createdAt || "",
        ]),
    ];
}

const PENDING_QUERY = gql`
    query HpCampaignPendingCaps {
        hpCampaignNextMonthPendingCaps {
            hpCampaignId
            school
            broker
            monthlyCap
            dailyCap
            costPerLead
            payPerImpression
            applyAt
            createdAt
        }
        hpCampaignTimedReleasePendingCaps {
            hpCampaignId
            school
            broker
            monthlyCap
            dailyCap
            costPerLead
            payPerImpression
            applyAt
            createdAt
        }
    }
`;

export default function SetCapsPage() {
    const [mode, setMode] = React.useState<CapsMode>("now");
    const [applyAt, setApplyAt] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [pendingLoading, setPendingLoading] = React.useState(false);
    const [result, setResult] = React.useState<BulkResult | null>(null);
    const [parseErrors, setParseErrors] = React.useState<string[]>([]);
    const [pasteText, setPasteText] = React.useState(HEADER_LINE + "\n");
    const [nextMonthPending, setNextMonthPending] = React.useState<PendingCap[]>([]);
    const [timedPending, setTimedPending] = React.useState<PendingCap[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        document.title = "Set Caps | DN";
    }, []);

    const loadPending = React.useCallback(async () => {
        setPendingLoading(true);
        try {
            const data = await query(PENDING_QUERY, {});
            setNextMonthPending((data?.hpCampaignNextMonthPendingCaps as PendingCap[]) || []);
            setTimedPending((data?.hpCampaignTimedReleasePendingCaps as PendingCap[]) || []);
        } catch (e: any) {
            toast.error(e?.message || "Failed to load pending caps");
        }
        setPendingLoading(false);
    }, []);

    React.useEffect(() => {
        loadPending();
    }, [loadPending]);

    const handleDownloadTemplate = () => {
        downloadCsv("set_caps_template.csv", [[...REQUIRED_HEADERS]]);
    };

    const submitText = async (text: string, failureLabel: string) => {
        if (mode === "schedule" && !applyAt.trim()) {
            toast.error("Schedule mode requires a date and time");
            return;
        }

        setLoading(true);
        setResult(null);
        setParseErrors([]);

        try {
            const {rows, parseErrors: localErrors} = parseCapsTsv(text);
            setParseErrors(localErrors);

            if (rows.length === 0) {
                toast.error(localErrors[0] || "No valid rows found");
                setLoading(false);
                return;
            }

            if (localErrors.length > 0) {
                toast.warning(`${localErrors.length} invalid row(s) skipped`);
            }

            const q = gql`
                mutation HpCampaignCapsBulkUpdate(
                    $mode: HpCapsBulkMode!
                    $rows: [HpCampaignCapUpdateInput!]!
                    $applyAt: String
                ) {
                    hpCampaignCapsBulkUpdate(mode: $mode, rows: $rows, applyAt: $applyAt) {
                        updatedCount
                        deletedQueueCount
                        messages
                        errors
                    }
                }
            `;

            const applyAtValue =
                mode === "schedule"
                    ? applyAt.length === 16
                        ? `${applyAt}:00`
                        : applyAt
                    : null;

            const response = await mutate(q, {mode, rows, applyAt: applyAtValue});
            if (!response?.hpCampaignCapsBulkUpdate) {
                setLoading(false);
                return;
            }

            const data = response.hpCampaignCapsBulkUpdate as BulkResult;
            setResult(data);
            const verb =
                mode === "now" ? "updated" : mode === "firstOfMonth" ? "staged for 1st" : "scheduled";
            toast.success(
                `${data.updatedCount} campaign(s) ${verb}` +
                    (data.deletedQueueCount ? `, ${data.deletedQueueCount} queue lead(s) deleted` : "")
            );
            await loadPending();
        } catch (e: any) {
            toast.error(e?.message || failureLabel);
        }

        setLoading(false);
    };

    const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }

        const text = await file.text();
        await submitText(text, "Failed to process file");
    };

    const handlePasteApply = async () => {
        await submitText(pasteText, "Failed to process paste");
    };

    return (
        <>
            <h2>Set Caps</h2>
            <p>
                Upload a file or paste tab-/comma-delimited rows. Header row must include (case sensitive):
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap"}}>
                    <code>{HEADER_LINE}</code>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                    >
                        Download header CSV
                    </Button>
                </Box>
            </p>

            <Box sx={{display: "flex", gap: 1, flexWrap: "wrap", mb: 2}}>
                <Chip
                    color={nextMonthPending.length ? "warning" : "default"}
                    label={`${nextMonthPending.length} pending for 1st of month`}
                />
                <Chip
                    color={timedPending.length ? "info" : "default"}
                    label={`${timedPending.length} pending timed release`}
                />
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadPending}
                    disabled={pendingLoading}
                >
                    Refresh pending
                </Button>
            </Box>

            <Accordion sx={{maxWidth: 900, mb: 2, boxShadow: "none", border: "1px solid", borderColor: "divider"}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>When modes explained</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography component="div" sx={{fontSize: 14}}>
                        <p style={{marginTop: 0}}>
                            <b>Now</b> — updates live caps immediately for uploaded campaigns only. Blank column =
                            leave the live value unchanged. May trim the pending lead queue when both{" "}
                            <code>monthly_cap</code> and <code>daily_cap</code> are provided as numbers.
                        </p>
                        <p>
                            <b>1st of month</b> — stages into the next-month queue (replaces the entire 1st queue on
                            upload). The monthly MySQL event applies on the 1st and{" "}
                            <b>zeros monthly caps for campaigns not in the staged list</b>. Blank columns are stored
                            as NULL (no override for that field).
                        </p>
                        <p style={{marginBottom: 0}}>
                            <b>Schedule</b> — upserts into the timed-release queue until the chosen date/time. A
                            minute cron applies each due row like <b>Now</b> (only those campaigns; no zeroing of
                            others). Re-uploading the same campaign replaces its pending timed row.
                        </p>
                    </Typography>
                </AccordionDetails>
            </Accordion>

            <Accordion sx={{maxWidth: 900, mb: 2, boxShadow: "none", border: "1px solid", borderColor: "divider"}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>
                        Q: What happens if you leave a column blank?
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography component="div" sx={{fontSize: 14}}>
                        <p style={{marginTop: 0}}>
                            Blank does <b>not</b> mean zero. It means “no value provided” for that column.
                        </p>
                        <p>
                            <b>Now</b> / <b>Schedule</b> (when applied) — blank columns leave the live campaign
                            value unchanged.
                        </p>
                        <p style={{marginBottom: 0}}>
                            <b>1st of month</b> — blank is stored as <code>NULL</code> in the staged next-month
                            table (no override for that field from this upload).
                        </p>
                    </Typography>
                </AccordionDetails>
            </Accordion>

            {loading ? (
                <Loading />
            ) : (
                <Box sx={{maxWidth: 900}}>
                    <Box sx={{display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2}}>
                        <FormControl size="small" sx={{minWidth: 180}}>
                            <InputLabel id="run-time-label">When</InputLabel>
                            <Select
                                labelId="run-time-label"
                                label="When"
                                value={mode}
                                onChange={(e) => {
                                    const next = e.target.value as CapsMode;
                                    setMode(next);
                                    if (next === "schedule" && !applyAt.trim()) {
                                        setApplyAt(defaultScheduleApplyAt());
                                    }
                                }}
                            >
                                <MenuItem value="now">Now</MenuItem>
                                <MenuItem value="firstOfMonth">1st of month</MenuItem>
                                <MenuItem value="schedule">Schedule</MenuItem>
                            </Select>
                        </FormControl>
                        {mode === "schedule" ? (
                            <TextField
                                size="small"
                                label="Apply at"
                                type="datetime-local"
                                value={applyAt}
                                onChange={(e) => setApplyAt(e.target.value)}
                                InputLabelProps={{shrink: true}}
                                sx={{minWidth: 240}}
                            />
                        ) : null}
                    </Box>

                    <Typography fontWeight={600} sx={{mb: 1}}>
                        Upload file
                    </Typography>
                    <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        sx={{mb: 3}}
                    >
                        Upload file
                        <VisuallyHiddenInput
                            ref={fileInputRef}
                            type="file"
                            accept=".txt,.tsv,.csv,text/plain"
                            onChange={handleFile}
                        />
                    </Button>

                    <Typography fontWeight={600} sx={{mb: 1}}>
                        Or paste
                    </Typography>
                    <Typography sx={{fontSize: 14, mb: 1, color: "text.secondary"}}>
                        Paste from Excel below the header (tabs between columns).
                    </Typography>
                    <TextField
                        label="Caps data"
                        multiline
                        minRows={8}
                        fullWidth
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                        inputProps={{spellCheck: false, style: {fontFamily: "monospace", fontSize: 13}}}
                        sx={{mb: 1.5}}
                    />
                    <Button variant="contained" onClick={handlePasteApply}>
                        Apply paste
                    </Button>
                </Box>
            )}

            {parseErrors.length > 0 ? (
                <Alert severity="warning" sx={{mt: 2, maxWidth: 900}}>
                    <div>Parse issues ({parseErrors.length}):</div>
                    <Box component="pre" sx={{m: 0, mt: 1, whiteSpace: "pre-wrap"}}>
                        {parseErrors.slice(0, 20).join("\n")}
                        {parseErrors.length > 20 ? `\n…and ${parseErrors.length - 20} more` : ""}
                    </Box>
                </Alert>
            ) : null}

            {result ? (
                <Box sx={{mt: 2, maxWidth: 900}}>
                    <Alert severity={result.errors.length ? "warning" : "success"} sx={{mb: 2}}>
                        Updated {result.updatedCount}
                        {result.deletedQueueCount
                            ? `; deleted ${result.deletedQueueCount} queued lead(s)`
                            : ""}
                        {result.errors.length ? `; ${result.errors.length} error(s)` : ""}
                    </Alert>
                    {result.messages.length ? (
                        <Box component="pre" sx={{whiteSpace: "pre-wrap", fontSize: 13}}>
                            {result.messages.join("\n")}
                        </Box>
                    ) : null}
                    {result.errors.length ? (
                        <Alert severity="error" sx={{mt: 1}}>
                            <Box component="pre" sx={{m: 0, whiteSpace: "pre-wrap"}}>
                                {result.errors.join("\n")}
                            </Box>
                        </Alert>
                    ) : null}
                </Box>
            ) : null}

            <Box sx={{mt: 4, maxWidth: 1100}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap"}}>
                    <Typography variant="h6" component="h3" sx={{m: 0}}>
                        Pending 1st of month ({nextMonthPending.length})
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        disabled={!nextMonthPending.length}
                        onClick={() =>
                            downloadCsv(
                                "pending_1st_of_month_caps.csv",
                                pendingToCsvRows(nextMonthPending, false)
                            )
                        }
                    >
                        Download CSV
                    </Button>
                </Box>
                <Typography sx={{fontSize: 13, color: "text.secondary", mb: 1}}>
                    Applied by the monthly MySQL event on the 1st. Campaigns not listed get monthly caps zeroed.
                </Typography>
                {pendingLoading ? (
                    <Loading />
                ) : nextMonthPending.length === 0 ? (
                    <Alert severity="info">No pending 1st-of-month caps.</Alert>
                ) : (
                    <Box sx={{overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Campaign</TableCell>
                                    <TableCell>School</TableCell>
                                    <TableCell>Broker</TableCell>
                                    <TableCell align="right">Monthly</TableCell>
                                    <TableCell align="right">Daily</TableCell>
                                    <TableCell align="right">CPL</TableCell>
                                    <TableCell align="right">PPI</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {nextMonthPending.map((r) => (
                                    <TableRow key={r.hpCampaignId}>
                                        <TableCell>{r.hpCampaignId}</TableCell>
                                        <TableCell>{r.school}</TableCell>
                                        <TableCell>{r.broker}</TableCell>
                                        <TableCell align="right">{fmtNum(r.monthlyCap)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.dailyCap)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.costPerLead)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.payPerImpression)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>

            <Box sx={{mt: 4, maxWidth: 1100, mb: 4}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap"}}>
                    <Typography variant="h6" component="h3" sx={{m: 0}}>
                        Pending timed release ({timedPending.length})
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        disabled={!timedPending.length}
                        onClick={() =>
                            downloadCsv(
                                "pending_timed_release_caps.csv",
                                pendingToCsvRows(timedPending, true)
                            )
                        }
                    >
                        Download CSV
                    </Button>
                </Box>
                <Typography sx={{fontSize: 13, color: "text.secondary", mb: 1}}>
                    Applied at the scheduled time by a minute cron, like Now (only listed campaigns; no zeroing).
                </Typography>
                {pendingLoading ? (
                    <Loading />
                ) : timedPending.length === 0 ? (
                    <Alert severity="info">No pending timed-release caps.</Alert>
                ) : (
                    <Box sx={{overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Apply at</TableCell>
                                    <TableCell>Campaign</TableCell>
                                    <TableCell>School</TableCell>
                                    <TableCell>Broker</TableCell>
                                    <TableCell align="right">Monthly</TableCell>
                                    <TableCell align="right">Daily</TableCell>
                                    <TableCell align="right">CPL</TableCell>
                                    <TableCell align="right">PPI</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {timedPending.map((r) => (
                                    <TableRow key={r.hpCampaignId}>
                                        <TableCell>{r.applyAt || ""}</TableCell>
                                        <TableCell>{r.hpCampaignId}</TableCell>
                                        <TableCell>{r.school}</TableCell>
                                        <TableCell>{r.broker}</TableCell>
                                        <TableCell align="right">{fmtNum(r.monthlyCap)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.dailyCap)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.costPerLead)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.payPerImpression)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>
        </>
    );
}
